import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {
  CubeLayerRemovalBlockedErrorResponse,
  CubeLayerRemovalDependency,
  CubeLayerRemovalDependencyCube,
} from 'types/api/SystemTenant/AROCube/CubeLayerSetTypes';

const titleId = 'cube-layer-removal-blocked-title';
const descriptionId = 'cube-layer-removal-blocked-description';

function cubeLabel(cube?: CubeLayerRemovalDependencyCube | null) {
  if (!cube) return null;
  if (cube.code && cube.name) return `${cube.code} · ${cube.name}`;
  return cube.name || cube.code;
}

function dependencyTypeLabel(type: string) {
  switch (type) {
    case 'PULL_MULTIPLE_LAYERS':
      return 'Pull multiple layers';
    case 'MAP_DATA_FROM_CUBE_TO_CUBE':
      return 'Map data between layers';
    case 'CUBE_PULL':
      return 'Cube pull';
    case 'CUBE_DEPENDENCY':
      return 'Cube dependency';
    case 'ACTIVE_BUDGET_GENERATION':
      return 'Budget generation';
    case 'ACTIVE_CUBE_CALCULATION':
      return 'Cube calculation';
    default:
      return type
        .toLowerCase()
        .split('_')
        .filter(Boolean)
        .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
        .join(' ');
  }
}

function DependencyCard({ dependency }: { dependency: CubeLayerRemovalDependency }) {
  const sourceCube = cubeLabel(dependency.sourceCube);
  const destinationCube = cubeLabel(dependency.destinationCube);
  const sourceLayer = dependency.sourceLayer?.name;
  const destinationLayer = dependency.destinationLayer?.name;

  return (
    <Paper variant='outlined' sx={{ p: 2, borderColor: '#E3EBF6', borderRadius: 1.5 }}>
      <Stack spacing={1.25}>
        <Stack direction='row' justifyContent='space-between' alignItems='center' spacing={1}>
          <Typography variant='body2' fontWeight={700} color='text.primary'>
            {dependencyTypeLabel(dependency.type)}
          </Typography>
        </Stack>

        {(sourceCube || destinationCube) && (
          <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1}>
            {sourceCube && <Typography variant='body2'>{sourceCube}</Typography>}
            {sourceCube && destinationCube && <ArrowForwardRoundedIcon color='action' fontSize='small' />}
            {destinationCube && <Typography variant='body2'>{destinationCube}</Typography>}
          </Stack>
        )}

        {(sourceLayer || destinationLayer) && (
          <Typography variant='caption' color='text.secondary'>
            Layer: {sourceLayer || 'Any layer'}
            {destinationLayer ? ` → ${destinationLayer}` : ''}
          </Typography>
        )}

        {dependency.attributes.length > 0 && (
          <Box>
            <Typography variant='caption' color='text.secondary' component='div' sx={{ mb: 0.75 }}>
              Affected attributes
            </Typography>
            <Stack direction='row' spacing={0.75} useFlexGap flexWrap='wrap'>
              {dependency.attributes.map((displayName, index) => (
                <Chip key={`${displayName}-${index}`} label={displayName} size='small' sx={{ bgcolor: '#EDF2FF' }} />
              ))}
            </Stack>
          </Box>
        )}
      </Stack>
    </Paper>
  );
}

export interface CubeLayerRemovalBlockedDialogProps {
  open: boolean;
  error: CubeLayerRemovalBlockedErrorResponse | null;
  fallbackLayerName?: string;
  onKeepLayer: () => void;
  onReviewMappings: () => void;
}

export function CubeLayerRemovalBlockedDialog(props: CubeLayerRemovalBlockedDialogProps) {
  const { open, error, fallbackLayerName, onKeepLayer, onReviewMappings } = props;
  const details = error?.details;
  const layerName = details?.layerName || fallbackLayerName || 'This layer';
  const dependencyCount = details?.dependencyCount ?? 0;
  const dependencies = details?.dependencies ?? [];
  const hasStructuredDetails = Boolean(error);

  return (
    <Dialog
      open={open}
      onClose={onKeepLayer}
      maxWidth='sm'
      fullWidth
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      PaperProps={{ role: 'alertdialog', sx: { borderRadius: 2 } }}
    >
      <DialogTitle id={titleId} sx={{ pb: 1 }}>
        <Stack direction='row' spacing={1.5} alignItems='center'>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              bgcolor: '#FFF4E5',
              color: '#B54708',
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
            }}
          >
            <WarningAmberRoundedIcon fontSize='small' />
          </Box>
          <Typography component='span' variant='h6' fontWeight={700}>
            {layerName} can’t be removed
          </Typography>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography id={descriptionId} variant='body2' color='text.secondary'>
            {hasStructuredDetails
              ? `${dependencyCount} ${dependencyCount === 1 ? 'mapping uses' : 'mappings use'} this layer. Update the ${
                  dependencyCount === 1 ? 'mapping' : 'mappings'
                } before removing it.`
              : 'This layer is still used by a cube mapping. Review the cube definitions before removing it.'}
          </Typography>

          {dependencies.length > 0 && (
            <Stack spacing={1.25} aria-label='Blocking cube mappings'>
              {dependencies.map((dependency, index) => (
                <DependencyCard
                  key={`${dependency.type}-${dependency.sourceCube?.id || 'source'}-${
                    dependency.destinationCube?.id || 'destination'
                  }-${index}`}
                  dependency={dependency}
                />
              ))}
            </Stack>
          )}

          {error?.requestId && (
            <Typography variant='caption' color='text.secondary'>
              Request ID: {error.requestId}
            </Typography>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button variant='main-table-panel-border' onClick={onReviewMappings}>
          Review mappings
        </Button>
        <Button variant='main-table-panel' onClick={onKeepLayer} autoFocus>
          Keep {details?.layerName || fallbackLayerName || 'layer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
